<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import type { ArticleDetail, MandatoryInfoItem, ImageInfo, ArticleFitment } from '../../types'
import * as api from '../../api/tecdocApi'
import MandatoryInfo from './MandatoryInfo.vue'
import ArticleDetailsView from './ArticleDetails.vue'
import ArticleFitmentTable from './ArticleFitmentTable.vue'
import SkeletonLoader from '../shared/SkeletonLoader.vue'

const props = defineProps<{
  sku: string
}>()

const isLoading = ref(true)
const error = ref<string | null>(null)
const details = ref<ArticleDetail | null>(null)
const mandatoryCriteria = ref<MandatoryInfoItem[]>([])
const showDetails = ref(false)
const hasData = ref(false)
const tecdocTitle = ref<{ manufacturer: string; description: string } | null>(null)

// Lazy-loaded cold fitment table (which vehicles/engines + VIN/year limits).
const fitment = ref<ArticleFitment | null>(null)
const showFitment = ref(false)
const fitmentLoading = ref(false)
const fitmentError = ref<string | null>(null)

async function toggleFitment() {
  if (showFitment.value) {
    showFitment.value = false
    return
  }
  showFitment.value = true
  if (fitment.value || fitmentLoading.value) return
  fitmentLoading.value = true
  fitmentError.value = null
  try {
    fitment.value = await api.getArticleFitment(props.sku)
  } catch (e: any) {
    fitmentError.value = e.message
  } finally {
    fitmentLoading.value = false
  }
}

const cloudZoomDataAttr = `appendSelector: '.picture-wrapper', zoomPosition: 'inside', zoomOffsetX: 0, captionPosition: 'bottom', tintOpacity: 0, zoomWidth: 244, zoomHeight: 257, easing: 3, touchStartDelay: true, zoomFlyOut: false, disableZoom: 'auto'`

function replaceTitleInPage(title: { manufacturer: string; description: string }) {
  const h1 = document.querySelector('.product-name h1')
  if (!h1 || !title.manufacturer) return

  const originalName = h1.textContent || ''
  const tecdocName = `${title.manufacturer} ${title.description}`.trim()

  h1.innerHTML = `${tecdocName}<span class="original-product-name" style="display:block;font-size:0.55em;font-weight:400;color:#888;margin-top:2px;">${originalName}</span>`
}

function injectImagesIntoCloudZoomGallery(images: ImageInfo[]) {
  if (images.length === 0) return

  const gallery = document.querySelector('.sevenspikes-cloudzoom-gallery')
  if (!gallery) return

  // Only replace if NopCommerce has no real image (shows "no-image" placeholder)
  const existingImg = gallery.querySelector('#cloudZoomImage') as HTMLImageElement | null
  if (existingImg && existingImg.src && !existingImg.src.includes('no-image') && !existingImg.src.includes('default-image')) {
    return // NopCommerce already has a real image, don't replace
  }

  const firstImg = images[0]
  const mainUrl = firstImg.imageUrl1600 ?? firstImg.imageUrl800 ?? firstImg.imageUrl400 ?? ''
  if (!mainUrl) return

  // Build picture-wrapper with main image
  const pictureWrapper = `<div class="picture-wrapper custom">
    <div class="picture" id="sevenspikes-cloud-zoom" data-zoomwindowelementid="" data-selectoroftheparentelementofthecloudzoomwindow="" data-defaultimagecontainerselector=".product-essential .gallery" data-zoom-window-width="244" data-zoom-window-height="257">
      <a data-full-image-url="${mainUrl}" class="picture-link" id="zoom1">
        <img src="${mainUrl}" alt="Artikkelbilde" class="cloudzoom" id="cloudZoomImage" itemprop="image" data-cloudzoom="${cloudZoomDataAttr}" style="user-select: none;">
      </a>
    </div>
  </div>`

  // Build thumbnails
  const thumbs = images.map(img => {
    const url = img.imageUrl1600 ?? img.imageUrl800 ?? img.imageUrl400 ?? ''
    const thumbCloudZoom = `appendSelector: '.picture-wrapper', zoomPosition: 'inside', zoomOffsetX: 0, captionPosition: 'bottom', tintOpacity: 0, zoomWidth: 244, zoomHeight: 257, useZoom: '.cloudzoom', image: '${url}', zoomImage: '${url}', easing: 3, touchStartDelay: true, zoomFlyOut: false, disableZoom: 'auto'`
    return `<a class="cloudzoom-gallery thumb-item" data-full-image-url="${url}" data-cloudzoom="${thumbCloudZoom}">
      <img class="cloud-zoom-gallery-img" src="${url}" alt="Artikkelbilde">
    </a>`
  }).join('')

  const pictureThumbs = images.length > 1
    ? `<div class="picture-thumbs custom">${thumbs}</div>`
    : ''

  // Replace gallery content
  gallery.innerHTML = pictureWrapper + pictureThumbs
}

onMounted(async () => {
  if (!props.sku) return

  try {
    const [mandatoryResult, imagesResult, titleResult] = await Promise.allSettled([
      api.getArticleMandatory(props.sku),
      api.getArticleImages(props.sku),
      api.getArticleTitle(props.sku),
    ])

    if (mandatoryResult.status === 'fulfilled') {
      mandatoryCriteria.value = mandatoryResult.value
    }

    // Inject TecDoc images into the CloudZoom gallery
    if (imagesResult.status === 'fulfilled' && imagesResult.value.length > 0) {
      nextTick(() => injectImagesIntoCloudZoomGallery(imagesResult.value))
    }

    // Replace product title with TecDoc title
    if (titleResult.status === 'fulfilled' && titleResult.value) {
      tecdocTitle.value = titleResult.value
      nextTick(() => replaceTitleInPage(titleResult.value))
    }

    // Show the enrichment block (and the fitment expander) for any TecDoc-matched product,
    // even when it has no "mandatory" criteria of its own.
    hasData.value = mandatoryCriteria.value.length > 0 || !!tecdocTitle.value
  } catch (e: any) {
    error.value = e.message
  } finally {
    isLoading.value = false
  }
})

async function loadFullDetails() {
  if (details.value) {
    showDetails.value = !showDetails.value
    return
  }
  try {
    details.value = await api.getArticleDetails(props.sku)
    showDetails.value = true
  } catch (e: any) {
    error.value = e.message
  }
}
</script>

<template>
  <div class="tecdoc-enrichment" v-if="!error && (isLoading || hasData)">
    <SkeletonLoader v-if="isLoading" type="text" :lines="2" />

    <template v-else>
      <!-- Viktig informasjon - always visible -->
      <div v-if="mandatoryCriteria.length > 0" class="tecdoc-enrichment__section">
        <h4 class="tecdoc-enrichment__heading">Viktig informasjon</h4>
        <MandatoryInfo :criteria="mandatoryCriteria" />
      </div>

      <!-- Toggle for full details -->
      <button
        v-if="hasData"
        class="tecdoc-enrichment__toggle"
        @click="loadFullDetails"
      >
        {{ showDetails ? 'Skjul TecDoc-detaljer' : 'Vis alle TecDoc-detaljer' }}
      </button>

      <!-- Full article details (expandable, no images — those go in gallery) -->
      <div v-if="showDetails && details" class="tecdoc-enrichment__details">
        <ArticleDetailsView :details="details" />
      </div>

      <!-- Cold fitment table: which vehicles/engines + VIN/year limits (lazy-loaded). -->
      <button class="tecdoc-enrichment__toggle" @click="toggleFitment">
        {{ showFitment ? 'Skjul kjøretøy / VIN' : 'Vis hvilke biler / VIN-begrensninger' }}
      </button>
      <div v-if="showFitment" class="tecdoc-enrichment__fitment">
        <SkeletonLoader v-if="fitmentLoading" type="text" :lines="3" />
        <p v-else-if="fitmentError" class="tecdoc-enrichment__error">{{ fitmentError }}</p>
        <ArticleFitmentTable
          v-else-if="fitment && fitment.groups.length > 0"
          :groups="fitment.groups"
          :truncated="fitment.truncated"
        />
        <p v-else-if="fitment" class="tecdoc-enrichment__empty">
          Ingen kjøretøydata tilgjengelig for denne delen.
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.tecdoc-enrichment {
  margin: 1rem 0;
  padding: 0 20px;
  max-width: 50%;

  &__section {
    margin-bottom: 0.5rem;
  }

  &__heading {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
    color: #333;
  }

  &__toggle {
    background: none;
    border: 1px solid #d1d5db;
    color: #002868;
    padding: 6px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    margin-top: 0.5rem;

    &:hover {
      background: #f0f4f8;
    }
  }

  &__details {
    margin-top: 1rem;
    border-top: 1px solid #e5e7eb;
    padding-top: 1rem;
  }

  &__fitment {
    margin-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
    padding-top: 0.75rem;
  }

  &__error {
    color: #b91c1c;
    font-size: 0.85rem;
  }

  &__empty {
    color: #6b7280;
    font-size: 0.85rem;
  }
}

@media (max-width: 768px) {
  .tecdoc-enrichment {
    max-width: 100%;
  }
}
</style>
